
import { useState, useRef, useEffect, useCallback } from 'react';

interface UseSearchDebounceOptions {
    initialValue?: string;
    delay?: number;
    onSearch: (query: string) => void;
}

interface UseSearchDebounceReturn {
    inputValue: string;
    setInputValue: (value: string) => void;
    handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    triggerSearchNow: () => void;
}

/**
 * useSearchDebounce
 * 
 * Custom hook to manage search input with:
 * 1. Debounce delay (default 3000ms) executed when typing stops.
 * 2. Immediate execution when 'Enter' is pressed.
 * 3. Prevention of double firing (clears debounce on Enter).
 */
export function useSearchDebounce({
    initialValue = '',
    delay = 3000,
    onSearch
}: UseSearchDebounceOptions): UseSearchDebounceReturn {
    const [inputValue, setInputValue] = useState(initialValue);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Clear timeout on cleanup
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    // Internal execution helper
    const executeSearch = useCallback((query: string) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        onSearch(query);
    }, [onSearch]);

    // Input Change Handler
    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setInputValue(newValue);

        // Clear existing timer if any
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        // Set new timer
        timeoutRef.current = setTimeout(() => {
            executeSearch(newValue);
        }, delay);
    }, [delay, executeSearch]);

    // Key Down Handler (Enter)
    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            // Cancel pending debounce and search immediately
            executeSearch(inputValue);
        }
    }, [inputValue, executeSearch]);

    // Manual Trigger
    const triggerSearchNow = useCallback(() => {
        executeSearch(inputValue);
    }, [inputValue, executeSearch]);

    return {
        inputValue,
        setInputValue,
        handleInputChange,
        handleKeyDown,
        triggerSearchNow
    };
}
